<template>
  <v-dialog v-model="showModalInternal" max-width="550">
    <v-card>
      <v-card-title>{{ trans(isUpdate ? 'misc.editMiscObject' : 'misc.newMiscObject') }}</v-card-title>
      <v-divider/>
      <v-card-text>
        <global-validation-errors class="mt-4" :value="validationErrors"/>
        <v-row>
          <v-col cols="5">
            <date-select
              :label="trans('misc.forSettlementMonthYear')"
              v-model="misc.date"
              type="month"
              @change="onDateSelect"
              :disabled="isUpdate"
            />
          </v-col>
          <v-col cols="7">
            <select-object
              :error-messages="validationErrors['objectId']"
              :disabled="isUpdate"
              :object-id.sync="misc.objectId"
            ></select-object>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <select-misc-account
                :misc-account-id.sync="misc.accountId"
            ></select-misc-account>
          </v-col>
          <v-col cols="6">
            <v-text-field
                :label="trans('misc.receiptField')"
                v-model="misc.receiptField"
                class="mt-0"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-text-field
                :label="trans('misc.description')"
                v-model="misc.description"
                :error-messages="validationErrors['description']"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <currency-input
                :error-messages="validationErrors['price']"
                :label="trans('misc.priceGross')"
                v-model="misc.price"
                currency="EUR"
                @change="misc.priceNet = null"
            ></currency-input>
          </v-col>
          <v-col cols="6">
            <currency-input
                :label="trans('misc.taxIfAvailable')"
                v-model="misc.tax"
                :hint="trans('general.optional')"
                persistent-hint
                currency="EUR"
                @change="misc.taxPercent = null"
            ></currency-input>
          </v-col>
        </v-row>
        <v-btn @click="showTaxGrossFromNetModal = true" elevation="0" small><v-icon small left>mdi-calculator</v-icon>{{ trans('misc.calculateTaxFromNet') }}&hellip;</v-btn>
      </v-card-text>
      <v-divider/>
      <v-card-actions>
        <v-spacer></v-spacer>
        <cancel-button @click="showModalInternal = false"></cancel-button>
        <save-button :loading="loading" @click="sendRequest"></save-button>
      </v-card-actions>
    </v-card>

    <tax-gross-from-net-modal
      :show-modal.sync="showTaxGrossFromNetModal"
      @calculation="setAmountsFromNetModal"
    />

    <confirm-modal
        :show-modal="duplicateErrorMessages != null"
        @close="duplicateErrorMessages = null"
        @confirm="forceDuplicate"
        :title="trans('misc.confirmDuplicate')"
        :confirm-button-label="trans('general.save')"
        confirm-button-color="error"
    >
      <div v-for="(message, i) in duplicateErrorMessages" :key="i">
        {{ message }}
      </div>
    </confirm-modal>

    <confirm-modal
      :show-modal.sync="positiveAmountConfirm.show"
      @confirm="onPositiveAmountConfirm"
      :title="trans('misc.confirmPositiveAmountTitle')"
      :confirm-button-label="trans('general.yes')"
    >
      {{ trans('misc.confirmPositiveAmount', {amount: renderCurrencyAmount(misc.price, 'EUR')}) }}
    </confirm-modal>
  </v-dialog>
</template>

<script>
import CancelButton from './CancelButton'
import {LocaleUtils} from '../utils/locale-utils';
import dayjs from 'dayjs';
import {RouterUtils} from '../utils/router-utils';
import SelectObject from '@/components/SelectObject';
import SelectMiscAccount from '@/components/SelectMiscAccount';
import CurrencyInput from '@/components/CurrencyInput';
import {MiscApi} from '@/api/misc-api';
import {MainUtils} from '@/utils/main-utils';
import GlobalValidationErrors from '@/components/GlobalValidationErrors';
import SaveButton from './SaveButton';
import ConfirmModal from './ConfirmModal';
import DateSelect from './DateSelect';
import TaxGrossFromNetModal from './TaxGrossFromNetModal';

export default {
  name: 'ObjectMiscAdditModal',
  mixins: [
    MainUtils,
    LocaleUtils,
    RouterUtils
  ],
  components: {
    TaxGrossFromNetModal,
    DateSelect,
    ConfirmModal,
    SaveButton,
    GlobalValidationErrors,
    CurrencyInput,
    SelectMiscAccount,
    SelectObject,
    CancelButton
  },
  props: {
    showModal: {
      type: Boolean
    },
    initialObjectMisc: {
      type: Object,
      required: false,
    }
  },
  data() {
    return {
      showModalInternal: false,
      loading: false,
      misc: this.getDefaultMisc(),
      validationErrors: {},
      duplicateErrorMessages: null,
      showTaxGrossFromNetModal: false,
      positiveAmountConfirm: {
        show: false,
        confirmed: false
      }
    }
  },
  computed: {
    isUpdate() {
      return this.initialObjectMisc && !!this.initialObjectMisc.miscId;
    }
  },
  methods: {
    getDefaultMisc() {
      let dayOfMonth = dayjs().date();
      let date = dayjs().startOf('month');

      if (dayOfMonth <= 15) {
        date = date.subtract(1, 'day');
      }

      return {
        date: date.format('YYYY-MM'),
        month: date.month() + 1,
        year: date.year(),
        objectId: null,
        accountId: null,
        receiptField: null,
        description: null,
        priceNet: null,
        taxPercent: null,
        price: null,
        tax: null
      }
    },
    convertMisc(objectMisc) {
      if (objectMisc == null) {
        return {};
      }

      let misc = JSON.parse(JSON.stringify(objectMisc))
      let date = dayjs(misc['settleDate']);

      misc.year = date.year();
      misc.month = date.month() + 1;
      misc.date = date.format('YYYY-MM');

      misc.accountId = misc.account ? misc.account.id : null;

      return misc;
    },
    sendRequest() {
      if (this.misc?.price > 0 && !this.positiveAmountConfirm.confirmed) {
        this.positiveAmountConfirm.show = true;
        return false;
      }

      if (!this.loading) {
        this.loading = true;

        let request = this.isUpdate ?
            MiscApi.updateObjectMisc(this.initialObjectMisc.miscId, this.misc) :
            MiscApi.createObjectMisc(this.misc);

        request.then(() => {
          this.$emit('success');
          this.showModalInternal = false;
        })
            .catch((response) => {
              this.validationErrors = this.getValidationErrors(response, false);
              if (this.validationErrors.duplicate) {
                this.duplicateErrorMessages = this.validationErrors.duplicate;
              }
            })
            .finally(() => {
              this.loading = false;
            });
      }
    },
    forceDuplicate() {
      this.misc.ignoreDuplicate = true;
      this.sendRequest();
    },

    onPositiveAmountConfirm () {
      this.positiveAmountConfirm.show = false;
      this.positiveAmountConfirm.confirmed = true;
      this.sendRequest();
    },

    onDateSelect (value) {
      let date = dayjs(value);

      this.misc.year = date.year();
      this.misc.month = date.month() + 1;
    },

    setAmountsFromNetModal (amounts) {
      this.misc.price = amounts.gross;
      this.misc.tax = amounts.tax;
    }
  },
  watch: {
    showModal: {
      immediate: true,
      handler: function (showModal) {
        this.showModalInternal = showModal;
        if (showModal) {
          this.misc = Object.assign(this.getDefaultMisc(), this.convertMisc(this.initialObjectMisc));
          this.positiveAmountConfirm.show = false;
          this.positiveAmountConfirm.confirmed = false;
        }
      }
    },
    showModalInternal(showModalInternal) {
      this.$emit('update:show-modal', showModalInternal);
      this.validationErrors = {};
    }
  }
}
</script>
